import React from "react";
import Icon from "./Icon";
import { BORDER_SOFT, BORDER_STRONG, TEXT, TEXT_DIM } from "./tokens";

export interface MobileCardProps {
  /** The record's identity (15px/500, single line with an ellipsis). */
  title: React.ReactNode;
  /** Up to two muted secondary lines. */
  subtitle?: React.ReactNode;
  subtitle2?: React.ReactNode;
  /** Right-aligned value (a price, a quantity). */
  trailing?: React.ReactNode;
  /** Muted line under the trailing value. */
  trailingSub?: React.ReactNode;
  /** Trailing chevron, default true when onClick is given. */
  chevron?: boolean;
  onClick?: () => void;
  /** Hide the bottom divider (last card of a list). */
  last?: boolean;
}

const LINE: React.CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/**
 * One list record: title over at most two secondary lines, an optional
 * trailing value and a chevron. Minimum height 64px, divider #EBF0F0 —
 * the same row the inventory-web list kit renders. Tap the whole row.
 */
export const MobileCard: React.FC<MobileCardProps> = ({
  title,
  subtitle,
  subtitle2,
  trailing,
  trailingSub,
  chevron,
  onClick,
  last,
}) => {
  const showChevron = chevron ?? !!onClick;
  return (
    <div
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      className="flex items-center"
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        gap: "12px",
        // A card is laid out by its parent (a column, or a two-up grid). Its
        // own nowrap lines must never set a min-content floor that widens that
        // parent's track — without this a long guest or location name pushes a
        // grid column past its share and the list scrolls sideways.
        minWidth: 0,
        minHeight: "64px",
        padding: "10px 0",
        borderBottom: last ? undefined : `1px solid ${BORDER_SOFT}`,
        cursor: onClick ? "pointer" : undefined,
        userSelect: "none",
      }}
    >
      <div className="flex flex-col" style={{ gap: "2px", minWidth: 0, flex: 1 }}>
        <span className="font-inter" style={{ fontSize: "15px", fontWeight: 500, color: TEXT, ...LINE }}>
          {title}
        </span>
        {subtitle && (
          <span className="font-inter" style={{ fontSize: "12.5px", color: TEXT_DIM, ...LINE }}>
            {subtitle}
          </span>
        )}
        {subtitle2 && (
          <span className="font-inter" style={{ fontSize: "12.5px", color: TEXT_DIM, ...LINE }}>
            {subtitle2}
          </span>
        )}
      </div>
      {(trailing || trailingSub) && (
        <div className="flex flex-col items-end" style={{ gap: "2px", flex: "none" }}>
          {trailing && (
            <span
              className="font-inter"
              style={{ fontSize: "14px", fontWeight: 500, color: TEXT, fontVariantNumeric: "tabular-nums" }}
            >
              {trailing}
            </span>
          )}
          {trailingSub && (
            <span
              className="font-inter"
              style={{ fontSize: "12.5px", color: TEXT_DIM, fontVariantNumeric: "tabular-nums" }}
            >
              {trailingSub}
            </span>
          )}
        </div>
      )}
      {showChevron && <Icon name="chev" color={BORDER_STRONG} size={16} />}
    </div>
  );
};

/** The container for MobileCards: a plain column, no gaps (rows carry their own dividers). */
export const MobileCardList: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex flex-col">{children}</div>
);

/** Label / value line inside a record sheet ("SKU   SKU-10000"). */
export const SheetField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center" style={{ gap: "12px", minWidth: 0, minHeight: "32px" }}>
    <span className="font-inter" style={{ fontSize: "13px", color: TEXT_DIM, flex: "none", width: "110px" }}>
      {label}
    </span>
    <div className="font-inter" style={{ fontSize: "14px", color: TEXT, flex: 1, minWidth: 0, wordBreak: "break-word" }}>
      {value}
    </div>
  </div>
);

export default MobileCard;
